import React, { useEffect, useMemo, useState } from "react";
import { Box, Typography, Button, Chip, Stack, Link } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CallMadeIcon from "@mui/icons-material/CallMade";
import DashBoardTable from "../reuseable-components/DashBoardTable";
import CreateEditVoteInfoModal from "./CreateEditVoteInfoModal";
import ConfirmModal from "../reuseable-components/ConfirmModal";
import {
  createVoteInfoBox,
  deleteVoteInfoBox,
  updateVoteInfoBox,
} from "../../../apis/admin-apis/dashboard-apis/VoteInfoTabApis";

const mapVoteInfoBox = (box) => ({
  id: box._id,
  order: box.displayOrder ?? 0,
  displayOrder: box.displayOrder ?? "",
  title: box.title || "-",
  description: box.body || "-",
  body: box.body || "",
  linkText: box.linkText || "-",
  url: box.linkUrl || "#",
  linkUrl: box.linkUrl || "",
  isVisible: Boolean(box.visible),
  visible: Boolean(box.visible),
});

const VoteInfoTab = ({ voteInfoBoxes = [], refreshDashboard }) => {
  const [voteBoxes, setVoteBoxes] = useState(() =>
    voteInfoBoxes.map(mapVoteInfoBox),
  );
  const [sortConfig, setSortConfig] = useState({
    key: "order",
    direction: "asc",
  });
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [isVoteInfoModalOpen, setIsVoteInfoModalOpen] = useState(false);
  const [selectedVoteInfo, setSelectedVoteInfo] = useState(null);
  const [confirmState, setConfirmState] = useState({
    open: false,
    voteInfoId: null,
  });
  const [isSavingVoteInfo, setIsSavingVoteInfo] = useState(false);
  const [isDeletingVoteInfo, setIsDeletingVoteInfo] = useState(false);

  useEffect(() => {
    setVoteBoxes(voteInfoBoxes.map(mapVoteInfoBox));
  }, [voteInfoBoxes]);

  const columns = [
    { id: "order", label: "Order", sortable: true },
    { id: "box", label: "Box", sortable: true },
    { id: "link", label: "Link", sortable: false },
    { id: "status", label: "Status", sortable: false },
    { id: "actions", label: "Actions", sortable: false },
  ];

  const sortedRows = useMemo(() => {
    const data = [...voteBoxes];

    if (sortConfig.key) {
      data.sort((a, b) => {
        const valueA = a[sortConfig.key];
        const valueB = b[sortConfig.key];

        if (valueA < valueB) {
          return sortConfig.direction === "asc" ? -1 : 1;
        }

        if (valueA > valueB) {
          return sortConfig.direction === "asc" ? 1 : -1;
        }

        return 0;
      });
    }

    return data;
  }, [sortConfig, voteBoxes]);

  const paginatedRows = useMemo(() => {
    const start = (page - 1) * rowsPerPage;

    return sortedRows.slice(start, start + rowsPerPage);
  }, [page, rowsPerPage, sortedRows]);

  const handleOpenCreateModal = () => {
    setSelectedVoteInfo(null);
    setIsVoteInfoModalOpen(true);
  };

  const handleOpenEditModal = (voteInfo) => {
    setSelectedVoteInfo(voteInfo);
    setIsVoteInfoModalOpen(true);
  };

  const handleCloseVoteInfoModal = () => {
    setIsVoteInfoModalOpen(false);
    setSelectedVoteInfo(null);
  };

  const handleSaveVoteInfo = async (voteInfoValues) => {
    setIsSavingVoteInfo(true);

    try {
      const payload = {
        title: voteInfoValues.title,
        body: voteInfoValues.body,
        linkText: voteInfoValues.linkText,
        linkUrl: voteInfoValues.linkUrl,
        visible: Boolean(voteInfoValues.visible),
      };

      if (voteInfoValues.displayOrder !== "") {
        payload.displayOrder = voteInfoValues.displayOrder;
      }

      if (selectedVoteInfo?.id) {
        const response = await updateVoteInfoBox(selectedVoteInfo.id, payload);
        const updatedBox = response?.box;

        if (updatedBox) {
          const mappedBox = mapVoteInfoBox(updatedBox);
          setVoteBoxes((prev) =>
            prev.map((voteInfo) =>
              voteInfo.id === selectedVoteInfo.id ? mappedBox : voteInfo,
            ),
          );
          if (refreshDashboard) refreshDashboard();
        }
      } else {
        const response = await createVoteInfoBox(payload);
        const createdBox = response?.box;

        if (createdBox) {
          setVoteBoxes((prev) => [...prev, mapVoteInfoBox(createdBox)]);
          if (refreshDashboard) refreshDashboard();
        }
      }

      handleCloseVoteInfoModal();
    } catch (error) {
      console.error("Failed to save vote info box:", error);
    } finally {
      setIsSavingVoteInfo(false);
    }
  };

  const handleOpenDeleteConfirm = (voteInfoId) => {
    setConfirmState({
      open: true,
      voteInfoId,
    });
  };

  const handleCloseConfirm = () => {
    setConfirmState({
      open: false,
      voteInfoId: null,
    });
  };

  const handleConfirmDelete = async () => {
    if (!confirmState.voteInfoId) {
      handleCloseConfirm();
      return;
    }

    setIsDeletingVoteInfo(true);

    try {
      const response = await deleteVoteInfoBox(confirmState.voteInfoId);

      if (response?.success) {
        setVoteBoxes((prev) =>
          prev.filter((voteInfo) => voteInfo.id !== confirmState.voteInfoId),
        );
        if (refreshDashboard) refreshDashboard();
      }
    } catch (error) {
      console.error("Failed to delete vote info box:", error);
    } finally {
      setIsDeletingVoteInfo(false);
      handleCloseConfirm();
    }
  };

  const rows = paginatedRows.map((row) => ({
    ...row,
    order: (
      <Typography sx={{ fontSize: "13px", color: "#333", pl: 1 }}>
        {row.order}
      </Typography>
    ),
    // Box column with Title and Description
    box: (
      <Box sx={{ py: 1, maxWidth: "450px" }}>
        <Typography
          sx={{ fontSize: "13px", fontWeight: 700, color: "#0F1E3D", mb: 0.5 }}
        >
          {row.title}
        </Typography>
        <Typography sx={{ fontSize: "12px", color: "#777", lineHeight: 1.4 }}>
          {row.body}
        </Typography>
      </Box>
    ),
    // External Link
    link: (
      <Link
        href={row.linkUrl}
        underline="hover"
        target="_blank"
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          color: "#D4A017",
          fontSize: "12px",
          fontWeight: 500,
        }}
      >
        {row.linkText} <CallMadeIcon sx={{ fontSize: "12px" }} />
      </Link>
    ),
    // Status Badge
    status: (
      <Chip
        label={row.visible ? "Visible" : "Hidden"}
        size="small"
        sx={{
          bgcolor: row.visible ? "#E8F5E9" : "#F3F4F6",
          color: row.visible ? "#2E7D32" : "#667085",
          fontSize: "11px",
          fontWeight: 600,
          borderRadius: "4px",
          height: "22px",
        }}
      />
    ),
    // Actions Group
    actions: (
      <Stack direction="row" spacing={1}>
        <Button
          variant="contained"
          size="small"
          onClick={() => handleOpenEditModal(row)}
          sx={{
            bgcolor: "#D4A017",
            textTransform: "none",
            fontSize: "11px",
            minWidth: "60px",
            height: "28px",
            "&:hover": { bgcolor: "#B8860B" },
          }}
        >
          Edit
        </Button>
        <Button
          variant="contained"
          size="small"
          onClick={() => handleOpenDeleteConfirm(row.id)}
          sx={{
            bgcolor: "#FFEAEA",
            color: "#D32F2F",
            textTransform: "none",
            fontSize: "11px",
            minWidth: "60px",
            height: "28px",
            boxShadow: "none",
            "&:hover": { bgcolor: "#FFDADA" },
          }}
        >
          Delete
        </Button>
      </Stack>
    ),
  }));

  return (
    <Box
      className="vote-info-tab-container"
      sx={{
        p: {
          xs: 1,
          md: 3,
        },
        bgcolor: "#fff",
      }}
    >
      {/* Header Section */}
      <Box
        className="tab-header"
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700, color: "#0F1E3D" }}>
          Vote Info Boxes
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenCreateModal}
          sx={{
            bgcolor: "#D4A017",
            "&:hover": { bgcolor: "#B8860B" },
            textTransform: "none",
            borderRadius: "6px",
            fontWeight: 600,
            px: 2,
          }}
        >
          New Box
        </Button>
      </Box>

      <Box sx={{ borderBottom: "1px solid #EAE2D1", mb: 3 }} />

      {/* Info Alert Box */}
      <Box
        className="info-alert"
        sx={{
          bgcolor: "#F8F9FA",
          borderLeft: "4px solid #D4A017",
          p: 2,
          mb: 4,
          borderRadius: "4px",
        }}
      >
        <Typography sx={{ fontSize: "13px", color: "#666" }}>
          These boxes appear on the public site under{" "}
          <strong>"Make Sure Your Voice is Heard."</strong> Add, edit, reorder,
          or remove them as needed. Hidden boxes (visibility off) won't show on
          the public site.
        </Typography>
      </Box>

      {/* Table Section */}
      <Box className="vote-info-table-wrapper">
        <DashBoardTable
          columns={columns}
          rows={rows}
          sortConfig={sortConfig}
          onSort={(key) =>
            setSortConfig({
              key,
              direction:
                sortConfig.key === key && sortConfig.direction === "asc"
                  ? "desc"
                  : "asc",
            })
          }
          page={page}
          totalPages={Math.ceil(sortedRows.length / rowsPerPage)}
          onPageChange={setPage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(v) => {
            setRowsPerPage(v);
            setPage(1);
          }}
          totalResults={sortedRows.length}
        />
      </Box>

      <CreateEditVoteInfoModal
        open={isVoteInfoModalOpen}
        handleClose={handleCloseVoteInfoModal}
        voteInfoData={selectedVoteInfo}
        onSave={handleSaveVoteInfo}
        isSaving={isSavingVoteInfo}
      />
      <ConfirmModal
        open={confirmState.open}
        handleClose={handleCloseConfirm}
        onConfirm={handleConfirmDelete}
        title="Delete Vote Info Box"
        message="Are you sure you want to delete this vote info box? This action cannot be undone."
        confirmLabel="Delete"
        loading={isDeletingVoteInfo}
      />
    </Box>
  );
};

export default VoteInfoTab;
